import { Box, CircularProgress, LinearProgress, Stack, Typography, useMediaQuery } from '@mui/material'
import { useEffect } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { screenPath } from '@commerce/core'
import { Screen } from '../design'
import { useDemo } from '../demo'
import { YALO_SEMANTIC } from '@commerce/tokens'

export interface ProcessingPageProps {
  /**
   * How long the screen waits before moving to the confirmation.
   *
   * The design waits 1.7 s. It is a prop rather than a constant because a test
   * should be able to assert the transition happens, not sleep through it —
   * and because the wait is theatre: the order is already recorded by the time
   * this screen mounts, so shortening it changes nothing but the pacing.
   */
  delayMs?: number
}

/**
 * The screen between "Sí, confirmar" and the confirmation.
 *
 * Nothing is in flight here. `PaymentPage` has already emptied the real cart
 * and recorded the order in demo state; this screen exists because the design
 * has it, and because a checkout that jumps straight from a tap to a receipt
 * reads as if it did not do anything.
 *
 * Reached without an order — a reload, a deep link — it sends the shopper back
 * to the cart rather than waiting 1.7 s to show a confirmation of nothing.
 */
export const ProcessingPage = ({ delayMs = 1700 }: ProcessingPageProps) => {
  const { slug = '', sessionId = '' } = useParams()
  const { distributor, lastOrderId } = useDemo()
  const navigate = useNavigate()
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  useEffect(() => {
    if (!lastOrderId) return
    const timer = setTimeout(
      // `replace` so Back from the confirmation returns to the catalog rather
      // than to a screen that would bounce the shopper forward again.
      () => navigate(screenPath('confirmed', { slug, sessionId }), { replace: true }),
      delayMs,
    )
    return () => clearTimeout(timer)
  }, [lastOrderId, delayMs, navigate, slug, sessionId])

  if (!lastOrderId) return <Navigate to={screenPath('cart', { slug, sessionId })} replace />

  return (
    <Screen testId="processing-page">
      <Stack
        spacing={2.5}
        sx={{ flex: 1, alignItems: 'center', justifyContent: 'center', p: 5, textAlign: 'center' }}
      >
        <CircularProgress
          size={56}
          thickness={4}
          aria-label="Confirmando tu pedido"
          // The spinner keeps its role and its label under reduced motion; only
          // the rotation stops. A frozen arc still reads as "busy"; a removed
          // progressbar would leave a screen reader with nothing.
          sx={prefersReducedMotion ? { animation: 'none', '& .MuiCircularProgress-circle': { animation: 'none' } } : undefined}
        />

        {/* One live region for both lines, so the announcement is a sentence
            rather than two interruptions. */}
        <Stack spacing={1} role="status">
          <Typography variant="h5" component="h1">
            Confirmando tu pedido…
          </Typography>
          <Typography variant="body2" sx={{ color: YALO_SEMANTIC.fg3 }}>
            Estamos enviándolo a {distributor.name}.
            <br />
            Esto toma unos segundos.
          </Typography>
        </Stack>

        {/* Decorative: the spinner above already carries the busy state, and a
            second progressbar in the accessibility tree says nothing new. It is
            the design's 1.6 s growing bar, so it is dropped entirely rather than
            frozen when the shopper has asked for less motion. */}
        {!prefersReducedMotion && (
          <Box aria-hidden sx={{ width: 180 }}>
            <LinearProgress sx={{ height: 5, borderRadius: 999 }} />
          </Box>
        )}
      </Stack>
    </Screen>
  )
}
